<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import BulkActionsToolbar from 'dashboard/components-next/Contacts/BulkActionsToolbar.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
});

const emit = defineEmits(['refresh-contacts']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);

// Selection state
const selectedContacts = ref([]);

// Computed properties for selection
const hasSelectedContacts = computed(() => selectedContacts.value.length > 0);
const allContactsSelected = computed(
  () =>
    props.contacts.length > 0 &&
    selectedContacts.value.length === props.contacts.length
);
const someContactsSelected = computed(
  () =>
    selectedContacts.value.length > 0 &&
    selectedContacts.value.length < props.contacts.length
);

// Selection handlers
const onContactSelectionChange = (contactId, isSelected) => {
  if (isSelected) {
    if (!selectedContacts.value.includes(contactId)) {
      selectedContacts.value.push(contactId);
    }
  } else {
    selectedContacts.value = selectedContacts.value.filter(
      id => id !== contactId
    );
  }
};

const toggleSelectAll = () => {
  if (allContactsSelected.value) {
    selectedContacts.value = [];
  } else {
    selectedContacts.value = props.contacts.map(contact => contact.id);
  }
};

const clearSelection = () => {
  selectedContacts.value = [];
};

const isContactSelected = contactId => {
  return selectedContacts.value.includes(contactId);
};

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

const refreshContacts = () => {
  emit('refresh-contacts');
};
</script>

<template>
  <div>
    <!-- Modern Selection Header - Always Visible -->
    <div
      v-if="contacts.length > 0"
      class="flex items-center justify-between px-6 py-4 bg-transparent"
    >
      <!-- Left side: Select All Checkbox -->
      <label class="flex items-center gap-3 cursor-pointer group">
        <div class="relative flex items-center">
          <input
            type="checkbox"
            :checked="allContactsSelected"
            :indeterminate="someContactsSelected"
            class="sr-only"
            @change="toggleSelectAll"
          />
          <!-- Custom Modern Checkbox -->
          <div
            class="w-5 h-5 rounded-lg border-2 transition-all duration-200 flex items-center justify-center group-hover:scale-105"
            :class="[
              allContactsSelected || someContactsSelected
                ? 'bg-gradient-to-br from-blue-500 to-blue-600 border-blue-500 shadow-lg shadow-blue-500/25'
                : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 group-hover:border-blue-400 dark:group-hover:border-blue-400',
            ]"
          >
            <!-- Checkmark for full selection -->
            <svg
              v-if="allContactsSelected"
              class="w-4 h-4 text-slate-800 dark:text-white"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <!-- Dash for partial selection -->
            <svg
              v-else-if="someContactsSelected"
              class="w-4 h-4 text-slate-800 dark:text-white"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M20 12H4"
              />
            </svg>
          </div>
        </div>
        <span
          class="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
        >
          {{ allContactsSelected ? 'Deselect all' : 'Select all' }}
        </span>
      </label>

      <!-- Right side: Selection Actions -->
      <div class="flex items-center gap-3">
        <!-- Selection Count -->
        <div
          class="transition-all duration-300 ease-in-out"
          :class="
            hasSelectedContacts ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          "
        >
          <span
            v-if="hasSelectedContacts"
            class="text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-full"
          >
            {{ selectedContacts.length }} selected
          </span>
        </div>

        <!-- Bulk Actions -->
        <div
          class="transition-all duration-300 ease-in-out flex items-center gap-2"
          :class="
            hasSelectedContacts
              ? 'opacity-100 scale-100'
              : 'opacity-0 scale-95 pointer-events-none'
          "
        >
          <BulkActionsToolbar
            v-if="hasSelectedContacts"
            :selected-contacts="selectedContacts"
            @clear-selection="clearSelection"
            @refresh-contacts="refreshContacts"
          />
        </div>
      </div>
    </div>

    <!-- Contacts List -->
    <div class="flex flex-col gap-4 px-6 pb-6">
      <ContactsCard
        v-for="contact in contacts"
        :id="contact.id"
        :key="contact.id"
        :name="contact.name"
        :email="contact.email"
        :thumbnail="contact.thumbnail"
        :phone-number="contact.phoneNumber"
        :additional-attributes="contact.additionalAttributes"
        :availability-status="contact.availabilityStatus"
        :is-expanded="expandedCardId === contact.id"
        :is-updating="isUpdating"
        :is-selected="isContactSelected(contact.id)"
        :show-selection="true"
        @toggle="toggleExpanded(contact.id)"
        @update-contact="updateContact"
        @show-contact="onClickViewDetails"
        @selection-change="onContactSelectionChange"
      />
    </div>
  </div>
</template>
